'use client'

import React, { ReactNode, useRef, useEffect, useState } from "react"
import { useSearchParams } from "next/navigation"
import { getSearchFnByObject } from "@/services/search"
import Map from "../ui/map/Map"
import { createObjectPagePathname, getMediaUrl } from "@/services/pathname"
import { environments, getNextSessions } from "@/services/holidayStay"
import { levels } from "@/services/bafad"

type ResultsMapProps = {
  aroeven: Aroeven,
  object: string,
  contextSearchParams: any
}

export default function ResultsMap({
  aroeven,
  object,
  contextSearchParams
}: ResultsMapProps) {

  const initResults = useRef(false)
  //const refMap = useRef<google.maps.Map>(null)

  const [map, setMap] = useState<google.maps.Map>(null)

  const [results, setResults] = useState(null)
  const [resultsFromState, setResultsFromState] = useState(null)
  const [boundaries, setBoundaries] = useState(null)

  const searchParams = useSearchParams()
  const searchFn = getSearchFnByObject(object)

  async function initData() {
    console.log('results_map initMap')
    let sp = new URLSearchParams({
      ...contextSearchParams,
      ...Object.fromEntries(searchParams),      
      batch: 1
    })
    let page: number = 1
    let fetch: boolean = true
    let results = []
    while (fetch === true) {
      try {
        sp.set('page', page.toString())
        const { items } = await searchFn(aroeven, sp)
        page = page + 1
        //console.log(items.length)
        if (items.length === 0) {
          fetch = false
        }
        results = [
          ...results,
          ...items
        ]
      }
      catch (error) {
        fetch = false
        console.log(error, 'error')
      }
    }
    setResultsFromState(null)
    setResults(results)
  }

  useEffect(() => {
    console.log('useeffect', window.history.state)
    if (typeof window !== 'undefined' && 'results_map' in window.history.state === true && 'results' in window.history.state.results_map === true) {
      console.log('state results_map', window.history.state.results_map)
      setResults(null)
      setResultsFromState(window.history.state.results_map.results)
      return
    }
    initData()
  }, [searchParams])

  useEffect(() => {
    if (initResults.current === false) {
      initResults.current = true
      return
    }
    if (results === null) return
    try {
      window.history.replaceState({
        ...history.state,
        results_map: {
          results
        },
        activeObject: null
      }, '')
    } 
    catch (error) {}
    console.log('useeffect results', [...results])
    initBounds()
  }, [results])

  useEffect(() => {
    if (resultsFromState === null) return
    initBounds()
  }, [resultsFromState])

  useEffect(() => {
    if (map === null) return
    console.log('setMap')
    initBounds()
    map.addListener('bounds_changed', () => {
      if (map.getCenter()) {
        try {
          history.replaceState({
            ...history.state,
            results_map: {
              ...(history.state.results_map && { ...history.state.results_map }),
              map: {
                zoom: map.getZoom(),
                center: {
                  latitude: map.getCenter().lat(),
                  longitude: map.getCenter().lng()
                }
              }
            }
          }, '')
        }
        catch (error) {}        
      }
    })
  }, [map])

  function initBounds() {
    console.log('initBounds')
    if (map === null) return
    if (resultsFromState !== null && history.state.results_map.map) {
      map.setCenter({
        lat: history.state.results_map.map.center.latitude,
        lng: history.state.results_map.map.center.longitude,
      })
      map.setZoom(history.state.results_map.map.zoom)
      return
    }
    const defaultBoundaries = { south: 43.126365452270235, west: -8.67604908273486, north: 50.06158816163058, east: 14.400768082734906 }
    let boundaries = defaultBoundaries
    if (results?.length > 0) {
      if (object === 'HolidayStay') {
        boundaries = {
          north: Math.max(...(results as HolidayStay[]).filter(holidayStay => holidayStay.accomodations.length > 0).map(holidayStay => holidayStay.accomodations[0].accomodation.address.latitude)),
          east: Math.max(...(results as HolidayStay[]).filter(holidayStay => holidayStay.accomodations.length > 0).map(holidayStay => holidayStay.accomodations[0].accomodation.address.longitude)),
          south: Math.min(...(results as HolidayStay[]).filter(holidayStay => holidayStay.accomodations.length > 0).map(holidayStay => holidayStay.accomodations[0].accomodation.address.latitude)),
          west: Math.min(...(results as HolidayStay[]).filter(holidayStay => holidayStay.accomodations.length > 0).map(holidayStay => holidayStay.accomodations[0].accomodation.address.longitude)),
        }
      }
      if (object === 'Bafad') {
        boundaries = {
          north: Math.max(...(results as Bafad[]).map(bafad => bafad.accomodation.address.latitude)),
          east: Math.max(...(results as Bafad[]).map(bafad => bafad.accomodation.address.longitude)),
          south: Math.min(...(results as Bafad[]).map(bafad => bafad.accomodation.address.latitude)),
          west: Math.min(...(results as Bafad[]).map(bafad => bafad.accomodation.address.longitude))
        }
      }
      if (object === 'Accomodation') {
        boundaries = {
          north: Math.max(...(results as Accomodation[]).map(accomodation => accomodation.address.latitude)),
          east: Math.max(...(results as Accomodation[]).map(accomodation => accomodation.address.longitude)),
          south: Math.min(...(results as Accomodation[]).map(accomodation => accomodation.address.latitude)),
          west: Math.min(...(results as Accomodation[]).map(accomodation => accomodation.address.longitude))
        }
      }
    }
    map.fitBounds(boundaries)
  }

  let markers = []

  if (object === 'HolidayStay') {
    markers = (resultsFromState ?? results ?? [] as HolidayStay[]).filter(holidayStay => holidayStay.accomodations.length > 0).map((holidayStay) => {
      console.log(holidayStay)
      const sessions = getNextSessions(holidayStay)
      return {
        position: {
          latitude: holidayStay.accomodations[0].accomodation.address.latitude,
          longitude: holidayStay.accomodations[0].accomodation.address.longitude
        },
        title: holidayStay.name,
        type: 'marker',
        infowindow: {
          content: `<div class="vignette map-info d-flex">
            <img src=` + getMediaUrl(holidayStay.medias.filter(media => media.mimeType.indexOf('image/') === 0)[0]?.url ?? '#') + ` alt="image" class="mapItem__img size-100" loading="lazy" />
            <div class="tag">
              <h3 class="tag-title">` + holidayStay.minAge + (holidayStay.minAge !== holidayStay.maxAge ? `-` + holidayStay.maxAge : ``) + `ans</h3>
              <div class="tag-tail"></div>
            </div>

            <div class="content mapItem__content d-flex flex-column justify-between ml-15">
              <div>
                <h4 class="fw-700 fs-5">` + holidayStay.name + `</h4>`
            + (holidayStay.accomodations.length > 0 ? `<div class="lieu d-flex items-center">` + holidayStay.accomodations[0].accomodation.address.city + ` (` + holidayStay.accomodations[0].accomodation.address.postCode.slice(0, 2) + `)</div>` : ``) +
            (holidayStay.environment in environments ? (`<div class="d-flex items-center">
                  <div class="type">` + environments[holidayStay.environment].name + `</div>
                </div>`) : ``) +
            `<div class="tarif">à partir de ` + Math.floor(Math.min(...sessions.map(session => Math.min(...session.departures.map(departure => departure.price)))) / 100) + `€ par enfant</div>
              </div>
            </div>
            <button class="gclose gbtn justify-content-start" tabindex="2" aria-label="Close"><i class="fi fi-rr-cross text-primary-3"></i></button>
          </div>`,
          target: createObjectPagePathname(object, holidayStay)
        },
        object: holidayStay
      }
    })
  }

  if (object === 'Bafad') {
    markers = (resultsFromState ?? results ?? [] as Bafad[]).map((bafad) => {
      return {
        position: {
          latitude: bafad.accomodation.address.latitude,
          longitude: bafad.accomodation.address.longitude
        },
        title: 'Bafa', //levels[bafad.level].name,
        infowindow: {
          content: `<div class="vignette map-info d-flex">
            <img src=` + getMediaUrl(bafad.medias.filter(media => media.mimeType.indexOf('image/') === 0)[0]?.url ?? '#') + ` alt="image" class="mapItem__img size-100" loading="lazy" />
            <div class="content mapItem__content d-flex flex-column justify-between ml-15">
              <div>
                <h4 class="fw-700 fs-5">` + 'Bafa' + `</h4>` //levels[bafad.level].name
            + `<div class="lieu d-flex items-center">` + bafad.accomodation.address.city + ` (` + bafad.accomodation.address.postCode.slice(0, 2) + `)</div>` +
            (bafad.level === 'bafa_qualification' && bafad.thematicLabel ? (`<div class="d-flex items-center">
                  <div class="type">` + bafad.thematicLabel + `</div>
                </div>`) : ``) +
            `<div class="tarif">à partir de ` + Math.floor(Math.min(...bafad.accomodationOptions.map(option => option.price)) / 100) + `€ par participant</div>
              </div>
            </div>
            <button class="gclose gbtn justify-content-start" tabindex="2" aria-label="Close"><i class="fi fi-rr-cross text-primary-3"></i></button>
          </div>`,
          target: createObjectPagePathname(object, bafad)
        },
        object: bafad
      }
    })
  }

  if (object === 'Accomodation') {
    markers = (resultsFromState ?? results ?? [] as Accomodation[]).map((accomodation) => {
      return {
        position: {
          latitude: accomodation.address.latitude,
          longitude: accomodation.address.longitude
        },
        title: accomodation.name,
        infowindow: {
          content: `<div class="vignette map-info d-flex">
            <img src=` + getMediaUrl(accomodation.medias.filter(media => media.mimeType.indexOf('image/') === 0)[0]?.url ?? '#') + ` alt="image" class="mapItem__img size-100" loading="lazy" />
            <div class="content mapItem__content d-flex flex-column justify-between ml-15">
              <div>
                <h4 class="fw-700 fs-5">` + accomodation.name + `</h4>`
            + `<div class="lieu d-flex items-center">` + accomodation.address.city + ` (` + accomodation.address.postCode.slice(0, 2) + `)</div>` +
            `</div>
            </div>
            <button class="gclose gbtn justify-content-start" tabindex="2" aria-label="Close"><i class="fi fi-rr-cross text-primary-3"></i></button>
          </div>`,
          target: createObjectPagePathname(object, accomodation)
        },
        object: accomodation
      }
    })
  }

  return (
    <>
      <Map
        markers={markers}
        {...boundaries !== null && { boundaries: boundaries }}
        clustering={true}
        setMap={setMap}
        setMappedMarkers={(markers: any[]) => {
          if (history.state.results_map && history.state.results_map.activeObject) {
            const marker = markers.filter(marker => marker.marker.object.id === history.state.results_map.activeObject.id)[0]
            if (marker) {
              new google.maps.event.trigger(marker.gmMarker, 'click')
            }
          }
        }}
        setActiveObject={(object: Searchable) => {
          console.log(object)
          try {
            history.replaceState({
              ...history.state,
              results_map: {
                ...(history.state.results_map && { ...history.state.results_map }),
                activeObject: object
              }
            }, '')
          }
          catch (error) {}          
        }}
      />
    </>
  )
}